import { useEffect, useRef } from "react";

/**
 * Periodically checks the server version endpoint.
 * If the server version changes, update the service worker without clearing
 * caches or reloading over an active form. The browser/service worker can
 * take the update on the next safe navigation.
 *
 * This is completely transparent — the user never has to hard-refresh or clear cache.
 */
export function useVersionCheck(intervalMs = 60_000) {
  const knownVersion = useRef<string | null>(null);
  const isReloading = useRef(false);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;

    async function forceServiceWorkerUpdate() {
      try {
        if ("serviceWorker" in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const reg of registrations) {
            await reg.update();
          }
        }
      } catch {
        // ignore
      }
    }

    async function check() {
      if (isReloading.current) return;
      try {
        const res = await fetch("/api/version", { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        const serverVersion = data.version;

        if (!knownVersion.current) {
          // First check — record the version without destroying the PWA cache.
          knownVersion.current = serverVersion;
          await forceServiceWorkerUpdate();
          return;
        }

        if (serverVersion !== knownVersion.current) {
          // New deployment detected — update in place and let the active page
          // finish safely. A later navigation picks up the new asset graph.
          console.log("[VersionCheck] New version available; preserving active work.");
          knownVersion.current = serverVersion;
          await forceServiceWorkerUpdate();
          window.dispatchEvent(new CustomEvent("app:update-available"));
        }
      } catch {
        // Network error — ignore
      }
    }

    // Check immediately, then every intervalMs
    check();
    timer = setInterval(check, intervalMs);

    return () => clearInterval(timer);
  }, [intervalMs]);
}
